import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit2, Trash2, Save, X, Loader2, Image, GripVertical, ExternalLink, Eye, EyeOff } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import AdminLogin from './AdminLogin';
import AdminHeader from './AdminHeader';
import { useAdminAuth } from '../hooks/useAdminAuth';

import { apiUrl, API_URL } from '../utils/config';
import logger from '../utils/logger';

const PartnersAdmin = ({ onBackToHome }) => {
  const { isAuth, isCheckingAuth, handleLogin, handleLogout } = useAdminAuth();
  
  const [partners, setPartners] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  
  const imageInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    logo: '',
    website: '',
    order: 0,
    active: true
  });

  const fetchPartners = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(apiUrl("partners/admin"));
      if (response.ok) {
        const data = await response.json();
        setPartners(data);
      }
    } catch (error) {
      logger.error('Error fetching partners:', error);
      toast.error('Failed to load partners');
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (isAuth) {
      fetchPartners();
    }
  }, [isAuth]);

  const resetForm = () => {
    setFormData({
      name: '',
      logo: '',
      website: '',
      order: partners.length,
      active: true
    });
    setEditingPartner(null);
    setShowForm(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo size must be less than 2MB');
      return;
    }

    setIsUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await fetch(apiUrl("upload"), {
        method: 'POST',
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, logo: `${API_URL}${data.url}` }));
        toast.success('Logo uploaded successfully!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload logo');
      }
    } catch (error) {
      logger.error('Upload error:', error);
      toast.error('Failed to upload logo');
    }
    setIsUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Partner name is required');
      return;
    }

    setIsSaving(true);
    
    const partnerData = {
      name: formData.name,
      logo: formData.logo || null,
      website: formData.website || null,
      order: parseInt(formData.order) || 0,
      active: formData.active
    };

    try {
      const url = editingPartner 
        ? apiUrl("partners/${editingPartner.id}")
        : apiUrl("partners");
      
      const response = await fetch(url, {
        method: editingPartner ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(partnerData)
      });

      if (response.ok) {
        toast.success(editingPartner ? 'Partner updated!' : 'Partner created!');
        resetForm();
        fetchPartners();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save partner');
      }
    } catch (error) {
      logger.error('Error saving partner:', error);
      toast.error('Failed to save partner');
    }
    
    setIsSaving(false);
  };

  const handleEdit = (partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name || '',
      logo: partner.logo || '',
      website: partner.website || '',
      order: partner.order || 0,
      active: partner.active !== false
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this partner?')) return;

    try {
      const response = await fetch(apiUrl("partners/${id}"), {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Partner deleted!');
        fetchPartners();
      } else {
        toast.error('Failed to delete partner');
      }
    } catch (error) {
      logger.error('Error deleting partner:', error);
      toast.error('Failed to delete partner');
    }
  };

  const toggleActive = async (partner) => {
    try {
      const response = await fetch(apiUrl("partners/${partner.id}"), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...partner,
          active: !partner.active
        })
      });

      if (response.ok) {
        toast.success(partner.active ? 'Partner hidden' : 'Partner visible');
        fetchPartners();
      }
    } catch (error) {
      logger.error('Error toggling partner:', error);
    }
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!isAuth) {
    return <AdminLogin onLogin={handleLogin} title="Partners Admin" />;
  }

  const activeCount = partners.filter(p => p.active).length;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="partners-admin">
      <AdminHeader
        title="Partners Admin"
        onBackToHome={onBackToHome}
        onLogout={handleLogout}
        onAddNew={() => { resetForm(); setShowForm(true); }}
        addNewLabel="Add Partner"
      />

      <div className="container mx-auto px-6 py-8">
        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-blue-800 text-sm">
            <strong>Tip:</strong> These logos appear in the scrolling banner under the hero section. 
            Use the "Order" field to control the display sequence. Toggle visibility to hide partners without deleting them.
          </p>
        </div>

        {/* Partner Grid */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <h2 className="font-semibold text-gray-900">Partner Logos ({activeCount} active / {partners.length} total)</h2>
          </div>
          
          {isLoading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
            </div>
          ) : partners.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <Image className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p>No partners yet. Click "Add Partner" to add your first one.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 p-4">
              {partners.map((partner) => (
                <div 
                  key={partner.id} 
                  className={`relative group border rounded-lg p-4 transition-all ${
                    partner.active 
                      ? 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-md' 
                      : 'bg-gray-50 border-gray-200 opacity-60'
                  }`}
                >
                  {/* Order Badge */}
                  <div className="absolute top-2 left-2 bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded">
                    #{partner.order}
                  </div>
                  
                  {/* Logo or Name */}
                  <div className="h-16 flex items-center justify-center mb-3 mt-4">
                    {partner.logo ? (
                      <img 
                        src={partner.logo} 
                        alt={partner.name}
                        className="max-h-full max-w-full object-contain"
                        onError={(e) => { 
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                    ) : null}
                    <span 
                      className={`text-lg font-bold text-gray-400 ${partner.logo ? 'hidden' : ''}`}
                      style={{ display: partner.logo ? 'none' : 'block' }}
                    >
                      {partner.name}
                    </span>
                  </div>
                  
                  {/* Name */}
                  <p className="text-center text-sm font-medium text-gray-700 truncate">
                    {partner.name}
                  </p>
                  
                  {/* Status */}
                  <div className="flex items-center justify-center mt-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      partner.active 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-500'
                    }`}>
                      {partner.active ? 'Visible' : 'Hidden'}
                    </span>
                  </div>
                  
                  {/* Actions - Show on hover */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                    <button
                      onClick={() => toggleActive(partner)}
                      className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                      title={partner.active ? 'Hide' : 'Show'}
                    >
                      {partner.active ? (
                        <EyeOff className="w-4 h-4 text-gray-600" />
                      ) : (
                        <Eye className="w-4 h-4 text-gray-600" />
                      )}
                    </button>
                    <button
                      onClick={() => handleEdit(partner)}
                      className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4 text-blue-600" />
                    </button>
                    <button
                      onClick={() => handleDelete(partner.id)}
                      className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="text-3xl font-bold text-blue-600">{partners.length}</div>
            <div className="text-gray-600">Total Partners</div>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="text-3xl font-bold text-green-600">{activeCount}</div>
            <div className="text-gray-600">Visible</div>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="text-3xl font-bold text-gray-400">{partners.length - activeCount}</div>
            <div className="text-gray-600">Hidden</div>
          </div>
        </div>
      </div>

      {/* Create/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {editingPartner ? 'Edit Partner' : 'Add New Partner'}
              </h2>
              <button
                onClick={resetForm}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Partner Name */}
              <div>
                <Label htmlFor="name">Partner Name <span className="text-red-500">*</span></Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., TechCorp Global"
                  required
                  className="mt-2"
                />
              </div>

              {/* Logo Upload */}
              <div>
                <Label>Logo Image</Label>
                <div className="mt-2">
                  {formData.logo ? (
                    <div className="flex items-center gap-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <img 
                        src={formData.logo} 
                        alt="Logo preview" 
                        className="h-12 w-auto max-w-[100px] object-contain"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-700">Logo uploaded</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => imageInputRef.current?.click()}
                        >
                          Replace
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setFormData(prev => ({ ...prev, logo: '' }))}
                          className="text-red-600"
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div
                      onClick={() => imageInputRef.current?.click()}
                      className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
                    >
                      {isUploading ? (
                        <Loader2 className="w-8 h-8 mx-auto text-blue-600 animate-spin" />
                      ) : (
                        <Image className="w-8 h-8 mx-auto text-gray-400" />
                      )}
                      <p className="mt-2 text-sm text-gray-600">
                        {isUploading ? 'Uploading...' : 'Click to upload logo'}
                      </p>
                      <p className="text-xs text-gray-400">PNG, SVG recommended (max 2MB)</p>
                    </div>
                  )}
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </div>
              </div>

              {/* Website URL */}
              <div>
                <Label htmlFor="website">Website URL (optional)</Label>
                <Input
                  id="website"
                  name="website"
                  type="url"
                  value={formData.website}
                  onChange={handleInputChange}
                  placeholder="https://example.com"
                  className="mt-2"
                />
              </div>

              {/* Order */}
              <div>
                <Label htmlFor="order">Display Order</Label>
                <Input
                  id="order"
                  name="order"
                  type="number"
                  min="0"
                  value={formData.order}
                  onChange={handleInputChange}
                  className="mt-2"
                />
                <p className="text-xs text-gray-400 mt-1">Lower numbers appear first</p>
              </div>

              {/* Active Toggle */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <input
                  type="checkbox"
                  id="active"
                  name="active"
                  checked={formData.active}
                  onChange={handleInputChange}
                  className="w-5 h-5 text-blue-600 rounded"
                />
                <Label htmlFor="active" className="cursor-pointer">
                  Show in scrolling banner
                </Label>
              </div>

              {/* Form Actions */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={resetForm}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSaving}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {editingPartner ? 'Update' : 'Add Partner'}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnersAdmin;
